import { useState } from "react"
import {
    Accordion, AccordionDetails, AccordionSummary, Alert, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle,
    IconButton, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Tooltip, Typography,
} from "@mui/material"
import DownloadIcon from "@mui/icons-material/Download"
import ExpandMoreIcon from "@mui/icons-material/ExpandMore"
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined"
import EnterpriseTable from "../../Table"
import { formatCount } from "./UsageTable"
import { downloadCsv } from "./exportCsv"
import EnterpriseMetricTooltip, { EnterpriseAdoptionTooltip, getMetricEnterprises } from "./EnterpriseMetricTooltip"

const ModuleAccessOverview = ({ data, onSelectEnterprise }) => {
    const [selectedModule, setSelectedModule] = useState(null)
    const modules = data.modules.filter(row => row.access_tracked)
        .sort((a, b) => b.accesses - a.accesses || a.label.localeCompare(b.label, "pt-BR"))
    const enterprises = selectedModule ? data.enterprises
        .filter(row => (row.modules[selectedModule.id]?.accesses || 0) > 0)
        .map(row => ({ ...row, accesses: row.modules[selectedModule.id].accesses }))
        .sort((a, b) => b.accesses - a.accesses || a.name.localeCompare(b.name, "pt-BR")) : []
    const columns = [
        { field: "name", headerName: "Empresa", flex: 1, minWidth: 200 },
        { field: "accesses", headerName: "Vezes acessadas", type: "number", width: 160 },
        { field: "details", headerName: "", width: 180, sortable: false, filterable: false,
            renderCell: ({ row }) => <Button size="small" onClick={() => {
                setSelectedModule(null)
                onSelectEnterprise(row)
            }}>Detalhes da empresa</Button> },
    ]

    return (
        <Accordion defaultExpanded disableGutters variant="outlined" sx={{ borderRadius: 2, "&::before": { display: "none" } }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />} aria-controls="module-access-overview-content" id="module-access-overview-heading">
                <Typography variant="h5">{data.enterprise_name ? "Acessos aos módulos da empresa" : "Visão geral de acessos aos módulos"}</Typography>
            </AccordionSummary>
            <AccordionDetails sx={{ p: { xs: 2, md: 3 } }} id="module-access-overview-content">
            <Paper variant="outlined" sx={{ p: { xs: 2, md: 3 }, borderRadius: 2, minWidth: 0 }}>
            <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
                <IconButton size="small" aria-label="Baixar CSV: visão geral de acessos" title="Baixar CSV: visão geral de acessos" onClick={() => downloadCsv("kwik-acessos-por-modulo.csv", [
                    { key: "label", label: "Módulo" }, { key: "accesses", label: "Vezes acessadas" }, { key: "accessed_count", label: "Empresas que acessaram" }, { key: "enabled_count", label: "Empresas habilitadas" }, { key: "adoption_rate", label: "Taxa de adoção (%)" },
                ], modules)}><DownloadIcon fontSize="small" /></IconButton>
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1, mb: 2 }}>
                Acesso é a abertura da guia do módulo. Uso é a atividade registrada no módulo pelas empresas habilitadas.
                Na Agenda, a abertura da guia também conta como uso.
                Taxa de adoção é a proporção de empresas habilitadas que tiveram uso no período.
                A coleta de acessos começa na publicação do contador; 20 entradas no mesmo dia somam 20 acessos.
                O ícone ao lado de cada módulo mostra seu critério de uso. Interações são registradas a partir desta versão;
                períodos anteriores usam apenas as evidências operacionais e históricos disponíveis.
            </Typography>
            {!data.summary.module_accesses && <Alert severity="info" sx={{ mb: 2 }}>Nenhum acesso registrado neste período. Não há histórico anterior à publicação do contador.</Alert>}
            <TableContainer sx={{ overflowX: "auto" }}>
                <Table size="small" aria-label="Visão geral de acessos aos módulos" sx={{ minWidth: 1120 }}>
                    <TableHead>
                        <TableRow>
                            <TableCell>Módulo</TableCell>
                            <TableCell align="right">Vezes acessadas</TableCell>
                            <TableCell align="right">Empresas que acessaram</TableCell>
                            <TableCell align="right">Empresas habilitadas</TableCell>
                            <TableCell align="right">Taxa de adoção</TableCell>
                            <TableCell align="right">Detalhes</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {modules.map(row => <TableRow key={row.id} hover>
                            <TableCell component="th" scope="row">
                                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                    {row.label}
                                    <Tooltip title={row.usage_rule || row.rule}>
                                        <InfoOutlinedIcon fontSize="small" color="action" tabIndex={0} aria-label={row.usage_rule || row.rule} />
                                    </Tooltip>
                                </Box>
                            </TableCell>
                            <TableCell align="right" sx={{ fontWeight: 700, color: "#21446C" }}><EnterpriseMetricTooltip
                                enterprises={getMetricEnterprises(data.enterprises, "modules", row.id, "accesses")}
                                getValue={enterprise => formatCount(enterprise.modules[row.id].accesses)}>{formatCount(row.accesses)}</EnterpriseMetricTooltip></TableCell>
                            <TableCell align="right"><EnterpriseMetricTooltip enterprises={getMetricEnterprises(data.enterprises, "modules", row.id, "accesses")}>{formatCount(row.accessed_count)}</EnterpriseMetricTooltip></TableCell>
                            <TableCell align="right"><EnterpriseMetricTooltip enterprises={getMetricEnterprises(data.enterprises, "modules", row.id, "enabled")}>{formatCount(row.enabled_count)}</EnterpriseMetricTooltip></TableCell>
                            <TableCell align="right"><EnterpriseAdoptionTooltip enterprises={data.enterprises} kind="modules" metricId={row.id}>
                                {row.adoption_rate == null ? "—" : `${row.adoption_rate.toLocaleString("pt-BR")} %`}
                            </EnterpriseAdoptionTooltip></TableCell>
                            <TableCell align="right"><Button size="small" disabled={!row.accessed_count} onClick={() => setSelectedModule(row)}>Ver empresas</Button></TableCell>
                        </TableRow>)}
                    </TableBody>
                </Table>
            </TableContainer>
            <Dialog open={Boolean(selectedModule)} onClose={() => setSelectedModule(null)} maxWidth="md" fullWidth aria-labelledby="module-access-details-title">
                <DialogTitle id="module-access-details-title">{selectedModule?.label} — acessos por empresa</DialogTitle>
                <DialogContent dividers>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>Empresas com acessos registrados no período consultado, ordenadas pela quantidade de vezes acessadas.</Typography>
                    <Box sx={{ overflowX: "auto" }}>
                        <EnterpriseTable rows={enterprises} columns={columns} pageSize={10} pagination toolbarFilter={false} />
                    </Box>
                </DialogContent>
                <DialogActions><Button onClick={() => setSelectedModule(null)}>Fechar</Button></DialogActions>
            </Dialog>
            </Paper>
            </AccordionDetails>
        </Accordion>
    )
}

export default ModuleAccessOverview
